import { useEffect, useState } from 'react';
import { FileText, Download, RefreshCw, Plus } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PageHeader from '../../../components/common/PageHeader';
import EmptyState from '../../../components/common/EmptyState';
import StatusChip from '../components/shared/StatusChip';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import PAMMErrorState from '../components/shared/PAMMErrorState';
import { DataTable } from '../../../components/ui/data-table';
import { pammReportService } from '../services/pammService';
import { formatDateTime, exportToCSV } from '../utils';
import type { PAMMReport } from '../types';
import { PAMM_REPORT_TYPE_OPTIONS } from '../constants';
import type { DataTableColumn } from '../../../components/ui/data-table';
import toast from 'react-hot-toast';

export default function Reports() {
  const [reports, setReports] = useState<PAMMReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setReports(await pammReportService.getAll());
    } catch {
      setError('Failed to load reports.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const newReport = await pammReportService.generate(
        'fund-001',
        'monthly',
        'January 2024'
      );
      setReports(prev => [newReport, ...prev]);
      toast.success('Report generated successfully!');
    } catch {
      toast.error('Failed to generate report.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = (report: PAMMReport) => {
    toast.success(`Downloading ${report.title}...`);
  };

  const columns: DataTableColumn<PAMMReport>[] = [
    {
      id: 'title',
      header: 'Report Title',
      accessor: row => <span className="text-xs font-medium">{row.title}</span>,
      sortable: true,
      sortValue: row => row.title,
    },
    {
      id: 'fund',
      header: 'Fund',
      accessor: row => <span className="text-xs">{row.fundName}</span>,
      sortable: true,
      sortValue: row => row.fundName,
    },
    {
      id: 'type',
      header: 'Type',
      accessor: row => <StatusChip status={row.type} size="sm" />,
    },
    {
      id: 'period',
      header: 'Period',
      accessor: row => <span className="text-xs">{row.period}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <StatusChip status={row.status} size="sm" />,
    },
    {
      id: 'size',
      header: 'Size',
      accessor: row => (
        <span className="text-xs text-muted-foreground">
          {row.fileSize ?? '—'}
        </span>
      ),
    },
    {
      id: 'generated',
      header: 'Generated',
      accessor: row => (
        <span className="text-xs text-muted-foreground">
          {row.generatedAt ? formatDateTime(row.generatedAt) : 'In Progress...'}
        </span>
      ),
      sortable: true,
      sortValue: row => row.generatedAt,
    },
    {
      id: 'by',
      header: 'Generated By',
      accessor: row => <span className="text-xs">{row.generatedBy}</span>,
    },
    {
      id: 'actions',
      header: '',
      accessor: row =>
        row.status === 'ready' ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-blue-600 hover:bg-blue-50"
            onClick={() => handleDownload(row)}
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
        ) : row.status === 'generating' ? (
          <RefreshCw className="h-3.5 w-3.5 text-muted-foreground animate-spin" />
        ) : null,
    },
  ];

  if (loading)
    return (
      <div className="space-y-4">
        <PageHeader title="Reports" />
        <LoadingSkeleton type="table" />
      </div>
    );
  if (error)
    return (
      <div className="space-y-4">
        <PageHeader title="Reports" />
        <PAMMErrorState message={error} onRetry={load} />
      </div>
    );

  return (
    <div className="space-y-6 pb-6">
      <PageHeader
        title="Reports"
        description="Generate and download performance, compliance, and analytics reports"
      >
        <Button size="sm" onClick={handleGenerate} disabled={generating}>
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          {generating ? 'Generating...' : 'Generate Report'}
        </Button>
      </PageHeader>
      {reports.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No Reports"
          description="Generate your first report to see it here."
        />
      ) : (
        <DataTable
          columns={columns}
          data={reports}
          searchValue={searchValue}
          showColumnVisibility={true}
          searchPlaceholder="Search reports..."
          onSearchChange={setSearchValue}
          onSearchReset={() => setSearchValue('')}
          filterFields={[
            { id: 'type', label: 'Type', options: PAMM_REPORT_TYPE_OPTIONS },
          ]}
          activeFilters={activeFilters}
          onFiltersChange={setActiveFilters}
          showExportButton={true}
          exportButtonText="Export CSV"
          onExportClick={() => exportToCSV(reports, 'pamm_reports')}
        />
      )}
    </div>
  );
}
